<template>
  <div>
    <Head title="Permohonan Beasiswa" />
    <h1 class="mb-8 text-2xl font-bold">Permohonan Beasiswa</h1>

    <!-- Tombol Aksi -->
    <div class="grid grid-cols-1 gap-2 mb-6 sm:grid-cols-2 md:grid-cols-4">
      <a v-if="can('write permohonan beasiswa')" class="text-center btn-green" href="/scholar-applications/download-surat-domisili" target="_blank">
        <font-awesome-icon icon="fas fa-print" class="mr-2" />Surat Keterangan Domisili
      </a>
      <a v-if="can('write permohonan beasiswa')" class="text-center btn-green" href="/scholar-applications/download-surat-permohonan" target="_blank">
        <font-awesome-icon icon="fas fa-print" class="mr-2" />Surat Permohonan
      </a>
      <a v-if="can('write permohonan beasiswa')" class="text-center btn-green" href="/scholar-applications/download-surat-pernyataan" target="_blank">
        <font-awesome-icon icon="fas fa-print" class="mr-2" />Surat Pernyataan
      </a>
      <Link v-if="can('write permohonan beasiswa')" class="text-center btn-indigo" href="/scholar-applications/create">
        <font-awesome-icon icon="fas fa-plus" class="mr-2" />Tambah
      </Link>
    </div>

    <!-- Kontainer responsif -->
    <div class="overflow-x-auto bg-white rounded-md shadow">
      <!-- Versi desktop -->
      <table class="hidden w-full text-sm md:table whitespace-nowrap">
        <thead>
          <tr class="font-bold text-left bg-gray-50">
            <th class="px-6 py-4">Periode Beasiswa</th>
            <th class="px-6 py-4">Tgl. Permohonan</th>
            <th class="px-6 py-4">Kriteria</th>
            <th class="px-6 py-4">Status</th>
            <th v-if="can('write permohonan beasiswa')" class="px-6 py-4">Aksi</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="application in applications.data" :key="application.id" class="border-t hover:bg-gray-50 focus-within:bg-gray-50">
            <td class="p-4">
              <Link :href="`/scholar-applications/${application.id}`" class="text-indigo-500 hover:underline">
                {{ application.periode }}
              </Link>
            </td>
            <td class="p-4">
              <Link :href="`/scholar-applications/${application.id}`" class="text-indigo-500 hover:underline">
                {{ application.tgl_permohonan }}
              </Link>
            </td>
            <td class="p-4">
              <Link :href="`/scholar-applications/${application.id}`" class="text-indigo-500 hover:underline">
                <slot name="kriteria">
                  <ul>
                    <li class="leading-6">Penghasilan Orang Tua : Rp. {{ $filters.formatNumber(application.penghasilan_orangtua) }}</li>
                    <li class="leading-6">Jumlah Tanggungan Orang Tua : {{ application.jumlah_tanggungan_orangtua }} Orang</li>
                    <li class="leading-6">Semester : {{ application.semester }}</li>
                    <li class="mt-2 leading-6">
                      Berprestasi :
                      <span v-if="application.is_berprestasi" class="px-3 py-1 text-sm bg-green-300 rounded-lg">Ya</span>
                      <span v-else class="px-3 py-1 text-sm bg-red-300 rounded-lg">Tidak</span>
                    </li>
                    <li class="mt-2 leading-6">
                      Prasejahtera :
                      <span v-if="application.is_prasejahtera" class="px-3 py-1 text-sm bg-green-300 rounded-lg">Ya</span>
                      <span v-else class="px-3 py-1 text-sm bg-red-300 rounded-lg">Tidak</span>
                    </li>
                  </ul>
                </slot>
              </Link>
            </td>
            <td class="p-4 text-center">
              <slot name="status">
                <ul>
                  <li>
                    <span v-if="application.status == 0" class="px-4 py-2 text-sm bg-yellow-300 rounded-lg">Draft</span>
                    <span v-else-if="application.status == 1" class="px-4 py-2 text-sm bg-yellow-500 rounded-lg">Diajukan</span>
                    <span v-else-if="application.status == 2" class="px-4 py-2 text-sm bg-red-300 rounded-lg">Belum Memenuhi Syarat</span>
                    <span v-else-if="application.status == 3" class="px-4 py-2 text-sm text-white bg-red-500 rounded-lg">Tidak Memenuhi Syarat</span>
                    <span v-else-if="application.status == 4" class="px-4 py-2 text-sm text-white bg-green-500 rounded-lg">Memenuhi Syarat</span>
                    <span v-else-if="application.status == 6" class="px-4 py-2 text-sm text-white bg-red-800 rounded-lg">Tidak Memenuhi Syarat Peringkat</span>
                    <span v-else class="px-4 py-2 text-sm text-white bg-green-500 rounded-lg">Disetujui</span>
                  </li>
                </ul>
                <p v-if="application.status == 2 || application.status == 3" class="mt-2 text-xs text-gray-600">{{ application.catatan_verifikasi }}</p>
              </slot>
            </td>
            <td v-if="can('write permohonan beasiswa')" class="p-4 text-center">
              <slot name="aksi">
                <div class="flex items-center justify-center gap-2">
                  <Link v-if="application.status == 0 || application.status == 2" class="btn-indigo-sm" :href="`/scholar-applications/${application.id}/edit`" title="Ubah">
                    <font-awesome-icon icon="fas fa-pencil" />
                  </Link>
                  <Link v-if="application.status == 0" class="btn-red-sm" @click.prevent="destroy(application.id)" title="Hapus">
                    <font-awesome-icon icon="fas fa-trash" />
                  </Link>
                </div>
              </slot>
            </td>
          </tr>
          <tr v-if="applications.data.length === 0">
            <td class="px-6 py-4 text-center text-gray-500" :colspan="can('write permohonan beasiswa') ? 5 : 4">
              Tidak ada data permohonan beasiswa.
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Versi mobile -->
      <div class="block divide-y md:hidden">
        <div v-for="application in applications.data" :key="application.id" class="p-4">
          <div class="flex items-start justify-between mb-2">
            <div>
              <Link :href="`/scholar-applications/${application.id}`" class="font-semibold text-indigo-600">
                {{ application.periode }}
              </Link>
              <p class="text-sm text-gray-600">{{ application.tgl_permohonan }}</p>
            </div>
            <div>
              <span v-if="application.status == 0" class="px-3 py-1 text-xs bg-yellow-300 rounded-lg">Draft</span>
              <span v-else-if="application.status == 1" class="px-3 py-1 text-xs bg-yellow-500 rounded-lg">Diajukan</span>
              <span v-else-if="application.status == 2" class="px-3 py-1 text-xs bg-red-300 rounded-lg">Belum Syarat</span>
              <span v-else-if="application.status == 3" class="px-3 py-1 text-xs text-white bg-red-500 rounded-lg">Tidak Syarat</span>
              <span v-else-if="application.status == 4" class="px-3 py-1 text-xs text-white bg-green-500 rounded-lg">Memenuhi</span>
              <span v-else-if="application.status == 6" class="px-3 py-1 text-xs text-white bg-red-800 rounded-lg">Tidak Peringkat</span>
              <span v-else class="px-3 py-1 text-xs text-white bg-green-600 rounded-lg">Disetujui</span>
            </div>
          </div>
          <ul class="space-y-1 text-sm text-gray-700">
            <li>Penghasilan: Rp. {{ $filters.formatNumber(application.penghasilan_orangtua) }}</li>
            <li>Tanggungan: {{ application.jumlah_tanggungan_orangtua }} Orang</li>
            <li>Semester: {{ application.semester }}</li>
            <li>Berprestasi: <span v-if="application.is_berprestasi" class="bg-green-300 px-2 py-0.5 text-xs rounded">Ya</span><span v-else class="bg-red-300 px-2 py-0.5 text-xs rounded">Tidak</span></li>
            <li>Prasejahtera: <span v-if="application.is_prasejahtera" class="bg-green-300 px-2 py-0.5 text-xs rounded">Ya</span><span v-else class="bg-red-300 px-2 py-0.5 text-xs rounded">Tidak</span></li>
          </ul>
          <div v-if="can('write permohonan beasiswa')" class="flex justify-end gap-2 mt-3">
            <Link v-if="application.status == 0 || application.status == 2" class="btn-indigo-sm" :href="`/scholar-applications/${application.id}/edit`">
              <font-awesome-icon icon="fas fa-pencil" />
            </Link>
            <Link v-if="application.status == 0" class="btn-red-sm" @click.prevent="destroy(application.id)">
              <font-awesome-icon icon="fas fa-trash" />
            </Link>
          </div>
        </div>
      </div>
    </div>

    <pagination class="mt-6" :links="applications.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'

export default {
  components: { Head, Link, Pagination },
  layout: Layout,
  props: { applications: Object },
  methods: {
    destroy(id) {
      this.$swal.fire({
        title: 'Anda yakin hapus data ini?',
        text: 'Anda tidak akan dapat mengembalikan data yang sudah dihapus!',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ya',
        cancelButtonText: 'Tidak',
      }).then((result) => {
        if (result.isConfirmed) {
          this.$inertia.delete(`/scholar-applications/${id}`)
        }
      })
    },
  },
}
</script>
